import { Avatar, Group, Stack, Text, ScrollArea } from '@mantine/core';
import { Users } from 'react-feather';
import { ContentCard } from '../../../../components/common/ContentCard';
import type { ActiveSessionParticipant } from '../../../../services/api/coachActiveSession';

/**
 * MED-792 AC8 / MED-1015 — right column "Participants" panel: a clean roster.
 *
 * Presence (the "· N in session" count + per-row dots) is gated on
 * connectedCount > 0 (MED-1015 AC3): while presence is stubbed (every
 * `is_connected` is false today) the roster reads clean — no misleading
 * "0 in session" or all-gray dots — and self-heals once real coach-roster
 * presence is wired (MED-1028).
 */

export interface ActiveSessionParticipantsPanelProps {
  participants: ActiveSessionParticipant[];
}

const ROSTER_PALETTE = [
  'medleyGreen',
  'warningAmber',
  'dangerRed',
  'groupAlpha',
  'groupBeta',
  'groupGamma',
  'surveyPurple',
] as const;

function pickColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return ROSTER_PALETTE[hash % ROSTER_PALETTE.length] ?? 'medleyGreen';
}

export function ActiveSessionParticipantsPanel({
  participants,
}: ActiveSessionParticipantsPanelProps) {
  const connectedCount = participants.filter(p => p.is_connected).length;
  // MED-1015 AC3 — only surface presence (count + dots) once someone is
  // actually connected; otherwise show a clean roster.
  const showPresence = connectedCount > 0;

  return (
    <ContentCard
      data-testid="active-session-participants-panel"
      headerDivider
      header={
        <Group gap={6} align="center" wrap="nowrap">
          <Users
            size={16}
            color="#6B7280"
            aria-hidden="true"
            style={{ position: 'relative', top: -1 }}
          />
          <Text fz={12} fw={600} c="#374151">
            Participants
          </Text>
          {showPresence && (
            <Text size="xs" c="dimmed">
              · {connectedCount} in session
            </Text>
          )}
        </Group>
      }
    >
      <ScrollArea offsetScrollbars style={{ flexGrow: 1 }}>
        {participants.length === 0 ? (
          <Text size="sm" c="dimmed">
            No participants in this group yet.
          </Text>
        ) : (
          <Stack gap={8}>
            {participants.map(p => (
              <Group key={p.user_id} justify="space-between" align="center">
                <Group gap={10} align="center" wrap="nowrap">
                  <Avatar
                    color={pickColor(p.user_id)}
                    radius="xl"
                    size="md"
                    data-testid="active-session-participant-avatar"
                  >
                    {p.initials}
                  </Avatar>
                  <Stack gap={0}>
                    <Text size="sm" fw={500}>
                      {p.display_name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Participant
                    </Text>
                  </Stack>
                </Group>
                {showPresence && (
                  <span
                    aria-label={
                      p.is_connected ? 'Connected' : 'Not yet connected'
                    }
                    title={p.is_connected ? 'Connected' : 'Not yet connected'}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: p.is_connected
                        ? 'var(--mantine-color-medleyGreen-6)'
                        : 'var(--mantine-color-gray-4)',
                      display: 'inline-block',
                    }}
                    data-testid="active-session-participant-presence-dot"
                  />
                )}
              </Group>
            ))}
          </Stack>
        )}
      </ScrollArea>
    </ContentCard>
  );
}
